/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * 🛡️ G2GENTE - ARQUIVO PROTEGIDO CONTRA REESCRITA POR IA
 * ═══════════════════════════════════════════════════════════════════════════════
 * SELO: G2GENTE-PROTECTED-2026-02-24-V1-AUTH
 * REGRAS: NÃO reescrever | APENAS search_replace | PRESERVAR estrutura
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to check if token is expired (without making a network request)
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    // Decode JWT token to check expiration
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    // Consider token expired if it will expire in less than 5 minutes
    return now >= (exp - 5 * 60 * 1000);
  } catch (e) {
    console.warn('Could not parse token:', e);
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    const storedToken = localStorage.getItem('token');
    // If token exists but is expired, clear it immediately
    if (storedToken && isTokenExpired(storedToken)) {
      localStorage.removeItem('token');
      return null;
    }
    return storedToken;
  });
  const [loading, setLoading] = useState(true);
  // State for multi-company management
  const [managedCompanies, setManagedCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const stored = localStorage.getItem('selectedCompany');
    return stored ? JSON.parse(stored) : null;
  });
  
  // Estado para o modal de troca de empresa
  const [companySwitchModal, setCompanySwitchModal] = useState({
    isOpen: false,
    targetCompany: null
  });
  const isMounted = useRef(true);
  const fetchInProgress = useRef(false);

  // Memoized fetchUser to prevent unnecessary re-creations
  const fetchUser = useCallback(async () => {
    // Prevent concurrent fetch requests
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;
    
    try {
      const currentToken = localStorage.getItem('token');
      if (!currentToken || isTokenExpired(currentToken)) {
        if (isMounted.current) {
          logout();
        }
        return;
      }
      
      // Ensure token is set in headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
      
      const response = await axios.get(`${API}/auth/me`);
      if (isMounted.current) {
        setUser(response.data);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      // Only logout on 401 (unauthorized) - not on network errors
      if (error.response?.status === 401) {
        if (isMounted.current) {
          logout();
        }
      } else {
        // On network errors, keep the token and try again later
        console.warn('Network error fetching user, keeping session');
      }
    } finally {
      fetchInProgress.current = false;
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);

  // Effect to handle token changes and cleanup
  useEffect(() => {
    isMounted.current = true;
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
      
      // Safety timeout - se após 10 segundos ainda estiver carregando, força o fim
      const safetyTimeout = setTimeout(() => {
        if (isMounted.current) {
          setLoading(false);
        }
      }, 10000);
      
      return () => {
        isMounted.current = false;
        clearTimeout(safetyTimeout);
      };
    } else {
      setLoading(false);
    }
    
    // Cleanup on unmount
    return () => {
      isMounted.current = false;
    };
  }, [token, fetchUser]);

  // Periodically check token validity (every 5 minutes)
  useEffect(() => {
    if (!token) return;
    
    const checkTokenValidity = () => {
      const currentToken = localStorage.getItem('token');
      if (currentToken && isTokenExpired(currentToken)) {
        console.log('Token expired, logging out');
        logout();
      }
    };
    
    const interval = setInterval(checkTokenValidity, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => clearInterval(interval);
  }, [token]);

  const login = async (identifier, password, role = null) => {
    try {
      const payload = { identifier, password };
      if (role) payload.role = role;
      
      const response = await axios.post(`${API}/auth/login`, payload);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      return userData;
    } catch (error) {
      // Check if it's a multiple profiles response
      if (error.response?.status === 300) {
        const { profiles } = error.response.data.detail;
        // Throw special error with profiles for UI to handle
        const multiProfileError = new Error('MULTIPLE_PROFILES');
        multiProfileError.profiles = profiles;
        throw multiProfileError;
      }
      throw error;
    }
  };

  // Login with CNPJ + user identifier (for company users)
  const loginWithCNPJ = async (cnpj, userIdentifier, password) => {
    try {
      const response = await axios.post(`${API}/auth/login-cnpj`, {
        cnpj,
        user_identifier: userIdentifier,
        password
      });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const register = async (cpf, email, password, role) => {
    const response = await axios.post(`${API}/auth/register`, { cpf, email, password, role });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const registerPartner = async (payload) => {
    const response = await axios.post(`${API}/auth/register-partner`, payload);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const updateCPF = async (cpf) => {
    const response = await axios.put(`${API}/auth/update-cpf`, { cpf });
    // Refresh user data
    const meResponse = await axios.get(`${API}/auth/me`);
    setUser({ ...user, cpf: meResponse.data.cpf, needs_cpf_update: false });
    return response.data;
  };

  // Fetch managed companies for recruiters
  const fetchManagedCompanies = useCallback(async () => {
    if (!token || !user || user.role !== 'COMPANY') return;
    try {
      const response = await axios.get(`${API}/companies/managed`);
      const companies = response.data.empresas_gerenciadas || [];
      setManagedCompanies(companies);
      // Não auto-seleciona mais a primeira empresa
      // O usuário deve escolher explicitamente
    } catch (error) {
      console.error('Error fetching managed companies:', error);
    }
  }, [token, user]);

  // Select a company to work with - abre modal de confirmação
  const selectCompany = (company) => {
    // Se é a mesma empresa, não faz nada
    if (company?.company_id === selectedCompany?.company_id) {
      return;
    }
    
    // Se está voltando para empresa principal (company = null)
    if (!company && !selectedCompany) {
      return;
    }
    
    // Abre modal de confirmação
    setCompanySwitchModal({
      isOpen: true,
      targetCompany: company
    });
  };
  
  // Confirma a troca de empresa
  const confirmCompanySwitch = () => {
    const company = companySwitchModal.targetCompany;
    
    // Salva no localStorage primeiro (antes do reload)
    if (company) {
      localStorage.setItem('selectedCompany', JSON.stringify(company));
    } else {
      localStorage.removeItem('selectedCompany');
    }
    
    // Recarrega imediatamente - NÃO atualiza state para evitar re-render e erros
    // O novo valor será lido do localStorage após o reload
    window.location.reload();
  };
  
  // Cancela a troca de empresa
  const cancelCompanySwitch = () => {
    setCompanySwitchModal({ isOpen: false, targetCompany: null });
  };

  // Clear selected company when logging out
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('selectedCompany');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setManagedCompanies([]);
    setSelectedCompany(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      loginWithCNPJ,
      register, 
      registerPartner,
      logout, 
      fetchUser, 
      updateCPF,
      // Multi-company management
      managedCompanies,
      selectedCompany,
      fetchManagedCompanies,
      selectCompany,
      // Company switch modal
      companySwitchModal,
      confirmCompanySwitch,
      cancelCompanySwitch
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
